import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TablePreviewProps {
  headers: string[];
  rows: string[][];
  maxRows?: number;
  defaultOpen?: boolean;
  className?: string;
}

export function TablePreview({
  headers,
  rows,
  maxRows = 3,
  defaultOpen = false,
  className,
}: TablePreviewProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const previewRows = rows.slice(0, maxRows);
  const hasMoreRows = rows.length > maxRows;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn("space-y-2", className)}
    >
      <div className="flex items-center justify-between">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="compact"
            className="text-muted-foreground hover:text-foreground"
          >
            {isOpen ? (
              <>
                <ChevronUp />
                Hide Preview
              </>
            ) : (
              <>
                <ChevronDown />
                Show Preview
              </>
            )}
          </Button>
        </CollapsibleTrigger>
        {hasMoreRows && (
          <span className="text-xs text-muted-foreground">
            Showing {previewRows.length} of {rows.length} rows
          </span>
        )}
      </div>

      <CollapsibleContent>
        <div className="rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                {headers.map((header, index) => (
                  <TableHead
                    key={index}
                    className="max-w-[200px] text-[11px] leading-tight whitespace-normal break-words align-middle py-3"
                    title={header}
                  >
                    {header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {previewRows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      className="max-w-[200px] text-[11px] leading-tight whitespace-normal break-words align-middle py-2"
                      title={cell}
                    >
                      {cell || "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {hasMoreRows && (
          <p className="mt-1 text-center text-xs text-muted-foreground">
            ... and {rows.length - maxRows} more rows
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

/*
 * Usage Example:
 *
 * import { TablePreview } from "@/components/table-preview";
 *
 * function ExtractedTableCard({ table }) {
 *   return (
 *     <Card>
 *       <CardHeader>
 *         <CardTitle>{table.name}</CardTitle>
 *       </CardHeader>
 *       <CardContent>
 *         <TablePreview
 *           headers={table.headers}
 *           rows={table.rows}
 *           maxRows={3}
 *         />
 *       </CardContent>
 *     </Card>
 *   );
 * }
 */
